import {
  GetActivitiesResponse,
  IPagingParams,
  Task,
  TasksResponse,
} from "@/types/types";
import { baseApi } from "./baseApi";

interface GetFileResponse {
  result: { url: string; exist: boolean; status: number; mime_type: string };
}

interface DeleteUserResponse {
  message: string;
}

interface GetFileRequest {
  recordId: string;
  fileHash: string;
}

export interface UserCreate {
  user_email: string;
  user_group: string;
  email_verified: boolean;
}

export interface UserCreateResponse {
  email: string;
  group: string;
  status: string;
}

export interface UserUpdateResponse {
  email: string;
  old_group: string;
  new_group: string;
}

export const fabricaApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getTasksByOperator: build.query<TasksResponse, IPagingParams>({
      query: (params) => ({
        url: `/back-fabrica-tasks/tasks`,
        params,
      }),
      providesTags: ["Tasks"],
    }),
    getTasks: build.query<TasksResponse, IPagingParams>({
      query: (params) => ({
        url: "/back-fabrica-tasks/my-tasks",
        params,
      }),
      providesTags: ["Tasks"],
    }),
    updateTask: build.mutation<Task, void>({
      query: () => ({
        url: "/back-fabrica-tasks/task/mutate",
      }),
      invalidatesTags: ["Tasks"],
    }),
    getFileHash: build.query<GetFileResponse, GetFileRequest>({
      query: (params) => ({
        url: `/back-fabrica-record/presigned_url/download/${params.fileHash}`,
        method: "GET",
      }),

      providesTags: (_, __, params) => [
        {
          type: "File",
          id: `${params.recordId}-${params.fileHash}`,
        },
      ],
    }),
    createFile: build.mutation<GetFileResponse, { file_hash: string }>({
      query: (body) => ({
        method: "POST",
        url: `/back-fabrica-record/presigned_url/upload`,
        body,
      }),
    }),
    delete: build.mutation<
      GetFileResponse,
      { expediente_id: string; delete_path: string; internal_id?: string }
    >({
      query: (body) => ({
        method: "DELETE",
        url: `/back-fabrica-record/records/${body.expediente_id}/${body.delete_path}`,
        body: { internal_id: body.internal_id },
      }),
      invalidatesTags: (_, __, params) => {
        if (params.internal_id) {
          return ["Activities", "Record"];
        }
        return ["Activities", "Records"];
      },
    }),
    getRecordActivities: build.query<
      GetActivitiesResponse,
      { expediente_id: string }
    >({
      query: (params) => ({
        method: "GET",
        url: `/back-fabrica-historian/historian/${params.expediente_id}`,
      }),
      providesTags: (_, __, params) => [
        { type: "Activities", id: params.expediente_id },
      ],
    }),
    getRoles: build.query<any, any>({
      query: () => ({
        method: "GET",
        url: `/back-fabrica-record/roles`,
      }),
    }),
    createUser: build.mutation<UserCreateResponse, UserCreate>({
      query: (body) => ({
        method: "POST",
        url: `/back-fabrica-authentication/users`,
        body,
      }),
      invalidatesTags: ["Users"],
    }),
    updateUser: build.mutation<
      UserUpdateResponse,
      { email: string; group: string }
    >({
      query: (params) => ({
        url: `back-fabrica-authentication/users/${params.email}`,
        method: "PUT",
        body: {
          group: params.group,
        },
      }),
      invalidatesTags: ["Users"],
    }),
    getUsers: build.query<any, any>({
      query: (params) => {
        const cleanParams = JSON.parse(JSON.stringify(params));

        const urlParams = new URLSearchParams(cleanParams);
        return {
          method: "GET",
          url: `/back-fabrica-authentication/users?${urlParams.toString()}`,
        };
      },
      providesTags: (_, __, params) => {
        return [{ type: "Users", id: params.group }];
      },
    }),
    deleteUser: build.mutation<DeleteUserResponse, { email: string }>({
      query: (body) => ({
        method: "DELETE",
        url: `/back-fabrica-authentication/users/${body.email}`,
      }),
      invalidatesTags: () => {
        return ["Users"];
      },
    }),
  }),
});

export const {
  useGetTasksQuery,
  useCreateFileMutation,
  useGetRecordActivitiesQuery,
  useGetTasksByOperatorQuery,
  useGetFileHashQuery,
  useGetRolesQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useGetUsersQuery,
  useDeleteMutation,
  useDeleteUserMutation,
} = fabricaApi;
